import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { scenariosAPI, reportsAPI } from '../lib/api';
import SolverPanel from './SolverPanel';
import { CreditorStatementCard } from '../components/CreditorStatementCard';
import { WaterfallResultsCard } from '../components/WaterfallResultsCard';
import { AforoCard } from '../components/AforoCard';
import { useProject } from '../lib/project-context';
import { RequiresProject } from '../components/RequiresContext';
import { UpsellCard } from '../components/ModuleSystem';
import { usePreferences } from '../lib/preferences-context';
import { useModules } from '../lib/modules-context';
import { useProjects, useScenarios, useRunAnalysis, usePreviewAnalysis, useAnalysisRuns, useSensitivity, useTornado, useCompareScenarios, useMarketSuggestions, useDuplicateScenario } from '../lib/queries';
import { extractErrorMessage, BACKEND_URL } from '../lib/api';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

/* ── KPI Tooltip definitions ── */
const KPI_TOOLTIPS = {
  'TIR Anual': { def: 'TIR del inversionista — rendimiento anualizado de −aportaciones + distribuciones.', formula: 'TIR = ((1 + IRR_mensual)^12 − 1) × 100. Se calcula sobre los flujos de caja mensuales del equity (aportaciones y distribuciones). Valor -1 indica capital mínimo (TIR no aplicable).', why: 'Es la métrica principal para comparar proyectos. Una TIR mayor al costo de oportunidad indica un proyecto rentable. Típico en desarrollo residencial MX: 15-25%.' },
  'VAN': { def: 'VAN del inversionista — flujo −aportaciones + distribuciones descontado al rendimiento exigido al capital (Mes 1 = hoy).', formula: 'VAN = Σ(flujo_equity_mes_t / (1 + tasa_descuento_mensual)^t) para t = 0 hasta n meses', why: 'VAN > 0 significa que el proyecto genera valor por encima de tu tasa de descuento. Es el indicador más confiable de viabilidad.' },
  'ROI': { def: 'Retorno sobre Inversión — porcentaje de ganancia respecto al capital total invertido.', formula: 'ROI = (Utilidad neta total / Capital comprometido o aportado) × 100', why: 'Mide la eficiencia del capital. Un ROI del 150% significa que por cada $100 invertidos, recuperas $250. No considera el tiempo.' },
  'MOIC': { def: 'Múltiplo sobre Capital Invertido — cuántas veces se multiplica tu inversión.', formula: 'MOIC = Σ flujos_positivos_equity / |Σ flujos_negativos_equity|', why: 'MOIC de 2x significa que duplicas tu capital. Importante para inversionistas que quieren saber el retorno absoluto.' },
  'Payback': { def: 'Período de recuperación — mes en que el flujo acumulado del equity se vuelve positivo.', formula: 'Payback = primer mes t donde flujo_operativo_acumulado ≥ 0 (excluye servicio de deuda)', why: 'Menor payback = menor riesgo. Se calcula sobre el flujo operativo (ingresos - egresos) sin incluir pagos de deuda.' },
  'Capital propio máximo': { def: 'Máximo del capital propio (equity) acumulado que el desarrollador aporta durante el proyecto. Es la base de ROI y MOIC.', formula: 'Capital propio máximo = máx(equity_acumulado_t) para todo t', why: 'Define cuánto capital propio necesitas tener disponible. No incluye la deuda.' },
  'Financiamiento máximo total': { def: 'Pico mensual de fondeo total del proyecto: capital propio acumulado más saldo del crédito en ese mismo mes.', formula: 'Financiamiento máximo total = máx(equity_acumulado_t + saldo_crédito_t); desglose = equity y deuda del mes del pico', why: 'Mide el tamaño total de la estructura de fondeo (capital + deuda) en el peor mes.' },
  'Peak Debt': { def: 'Deuda máxima viva — el punto de mayor apalancamiento durante el proyecto.', formula: 'Peak Debt = máx(saldo_crédito_mes_t) para todo t', why: 'Define el tamaño del crédito necesario. A mayor peak debt, mayor costo financiero y riesgo de refinanciamiento.' },
  'Mes Crítico Liquidez': { def: 'Mes con el menor flujo de caja acumulado — el punto de mayor vulnerabilidad financiera.', formula: 'Mes Crítico = t donde flujo_acumulado_t es mínimo', why: 'Requiere tener el capital disponible en ese momento. Es tu momento de mayor riesgo.' },
  'Margen Bruto': { def: 'Porcentaje de ganancia antes de gastos financieros e indirectos.', formula: 'Margen Bruto = (Ingresos − Terreno − Costos duros − Costos blandos) / Ingresos × 100', why: 'Mide la eficiencia operativa del proyecto. Típico en desarrollo residencial: 30-45%.' },
  'Margen Neto': { def: 'Porcentaje de ganancia final después de todos los costos incluyendo financieros.', formula: 'Margen Neto = (Ingresos − Costos totales − Intereses − Indirectos) / Ingresos × 100', why: 'Es la rentabilidad real del proyecto. Un margen neto < 15% es riesgoso; > 25% es sólido.' },
  'P.E. Ingresos': { def: 'Punto de equilibrio en ingresos — monto de ventas necesario para cubrir todos los costos.', formula: 'PE Ingresos = Costos totales / (1 − ratio_costos_variables). Se calcula cuando ingresos acumulados ≥ costos acumulados.', why: 'Por debajo de este monto, hay pérdida. Indica tu margen de seguridad.' },
  'P.E. Unidades': { def: 'Punto de equilibrio en unidades — cuántas unidades debes vender para cubrir costos.', formula: 'PE Unidades = PE Ingresos / precio_promedio_por_unidad', why: 'Si necesitas vender 80% de las unidades para el breakeven, el riesgo es alto.' },
  'Utilidad / Unidad': { def: 'Ganancia neta promedio por cada unidad vendida.', formula: 'Utilidad/Unidad = Utilidad neta total / Total de unidades', why: 'Permite evaluar si el margen por unidad es suficiente para justificar el proyecto.' },
  'LTV': { def: 'Loan to Value — porcentaje del valor del proyecto financiado con deuda.', formula: 'LTV = Peak Debt / Ingreso total × 100', why: 'Los bancos típicamente limitan el LTV a 65-70%. Un LTV alto indica mayor dependencia del crédito.' },
  'LTC': { def: 'Loan to Cost — porcentaje del costo total financiado con deuda.', formula: 'LTC = Peak Debt / Costo total × 100', why: 'Mide el apalancamiento sobre el costo. Un LTC > 65% indica dependencia excesiva de deuda.' },
  'Cash on Cash': { def: 'Rendimiento del efectivo invertido — utilidad sobre el capital propio total.', formula: 'Cash on Cash = Utilidad neta / Capital propio máximo requerido × 100', why: 'Mide el rendimiento real sobre el dinero que pusiste de tu bolsa, sin considerar el tiempo.' },
  'Yield on Cost': { def: 'Rendimiento sobre costo total del proyecto.', formula: 'Yield on Cost = Utilidad neta / Costo total × 100', why: 'Útil para comparar la eficiencia de diferentes proyectos independientemente de su estructura de capital.' },
  'Utilidad / m²': { def: 'Ganancia neta por metro cuadrado vendible.', formula: 'Utilidad/m² = Utilidad neta / Σ(area_m2 × cantidad) de todas las tipologías', why: 'Permite comparar la rentabilidad por superficie entre proyectos de diferentes tamaños.' },
  'Duración': { def: 'Duración total del proyecto en meses.', formula: 'Duración = meses_preventa + meses_construcción + meses_post_construcción', why: 'Mayor duración implica mayor exposición a riesgo de mercado, inflación y costo financiero.' },
  'VAN del proyecto': { def: 'Valor presente del negocio inmobiliario sin crédito: terreno, obra y venta.', formula: 'VAN = Σ flujo_operativo_t / (1 + tasa_proyecto_mensual)^t, con t = 0 en el mes 1 (Mes 1 = hoy).', why: 'Mide si el proyecto crea valor antes de financiarlo. La diferencia con el VAN del inversionista es el efecto del crédito en valor presente.' },
  'TIR del proyecto': { def: 'Rendimiento anual del flujo sin crédito.', formula: 'TIR = (1 + TIR_mensual)^12 − 1, sobre ingresos − costos.', why: 'Si supera la tasa del proyecto, el negocio rinde más que su costo de capital.' },
  'VAN del inversionista': { def: 'Valor presente de lo que aporta y recibe el inversionista.', formula: 'VAN = Σ (−aportación_t + distribución_t) / (1 + rendimiento_exigido_mensual)^t, con t = 0 en el mes 1. La caja se retiene en el proyecto hasta el último mes con obligaciones.', why: 'VAN > 0: tu capital rinde más de lo que le exiges.' },
  'TIR del inversionista': { def: 'Rendimiento anual del capital aportado.', formula: 'TIR = (1 + TIR_mensual)^12 − 1, sobre −aportaciones + distribuciones.', why: 'Si el flujo cambia de signo varias veces, la TIR no es confiable: usa la MIRR.' },
  'MIRR': { def: 'TIR modificada: financia las aportaciones a la tasa exigida y reinvierte las distribuciones a la tasa de reinversión.', formula: 'MIRR = (VF distribuciones ÷ VP aportaciones)^(1/n) − 1, anualizada.', why: 'Da un rendimiento único aunque el flujo cambie de signo varias veces.' },
};
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Play, Settings2, RefreshCw, TrendingUp, TrendingDown, DollarSign, Percent, Calendar, AlertTriangle, Target, Wallet, BarChart3, Loader2, Info, ArrowUpRight, ArrowDownRight, Plus, Copy, Download, FileText } from 'lucide-react';
import { formatCurrency, formatPercent, formatNumber } from '../lib/utils';
import { getReturnMetric } from '../lib/returnMetric';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  ComposedChart, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, ReferenceLine
} from 'recharts';
import { CorridaFinanciera } from '../components/CorridaFinanciera';
import { DecisionEngine } from '../components/DecisionEngine';
import { EngineBanner } from '../components/EngineBanner';
import StressTestPanel from '../components/StressTestPanel';

/* ═══════════════════════════════════════════════════════════════
   KPI Card
   ═══════════════════════════════════════════════════════════════ */
function KPICard({ label, value, format = 'currency', icon: Icon, subtitle, trend, highlight, showTooltips = true, delta }) {
  let displayValue;
  if (value === null || value === undefined) displayValue = '—';
  else if (format === 'currency') displayValue = formatCurrency(value);
  else if (format === 'percent') displayValue = formatPercent(value);
  else if (format === 'month') displayValue = `Mes ${value}`;
  else if (format === 'units') displayValue = `${formatNumber(value, 1)} uds`;
  else if (format === 'multiple') displayValue = `${formatNumber(value, 2)}x`;
  else displayValue = String(value);

  const isPositive = typeof value === 'number' && value > 0;
  const tooltip = KPI_TOOLTIPS[label];

  return (
    <Card className={`rounded-xl border-border/50 shadow-sm transition-[box-shadow] duration-200 hover:shadow-md ${highlight ? 'ring-1' : ''}`}
      style={highlight ? { borderColor: 'var(--module-primary)', ringColor: 'var(--module-light)' } : {}}
      data-testid={`kpi-${label.toLowerCase().replace(/[\s\/]/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium truncate">{label}</p>
              {tooltip && showTooltips && (
                <TooltipProvider delayDuration={200}>
                  <UITooltip>
                    <TooltipTrigger asChild>
                      <Info className="w-3 h-3 text-muted-foreground/40 hover:text-muted-foreground cursor-help shrink-0" />
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[320px] p-3" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-default)', color: 'var(--text-primary)' }}>
                      <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{label}</p>
                      <p className="text-[11px] leading-relaxed mb-2" style={{ color: 'var(--text-secondary)' }}>{tooltip.def}</p>
                      <p className="text-[10px] font-mono px-2 py-1 rounded mb-2" style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }}>{tooltip.formula}</p>
                      <p className="text-[10px] leading-relaxed" style={{ color: 'var(--text-muted)' }}>{tooltip.why}</p>
                    </TooltipContent>
                  </UITooltip>
                </TooltipProvider>
              )}
            </div>
            <p className="text-xl md:text-2xl font-display font-semibold tabular-nums mt-1 truncate">{displayValue}</p>
            {delta && (
              <span className="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded-full inline-block mt-1 animate-pulse" style={{
                background: delta.delta > 0 ? 'var(--success-soft)' : 'var(--danger-soft)',
                color: delta.delta > 0 ? 'var(--success)' : 'var(--danger)',
              }}>
                {format === 'currency' ? `${delta.delta > 0 ? '+' : ''}$${Math.abs(delta.delta).toLocaleString('es-MX', { maximumFractionDigits: 0 })}` :
                 format === 'percent' ? `${delta.delta > 0 ? '+' : ''}${delta.delta.toFixed(1)}%` :
                 `${delta.delta > 0 ? '+' : ''}${delta.delta.toFixed(1)}`}
              </span>
            )}
            {subtitle && <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          {Icon && (
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: isPositive ? 'var(--success-soft)' : 'var(--danger-soft)' }}>
              <Icon className="h-4 w-4" style={{ color: isPositive ? 'var(--success)' : 'var(--danger)' }} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Chart tooltip
   ═══════════════════════════════════════════════════════════════ */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-border bg-card/95 backdrop-blur shadow-lg px-4 py-3 text-xs">
      <p className="font-medium text-foreground mb-1.5">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 py-0.5">
          <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-medium tabular-nums">{formatCurrency(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const CHART_GRID = "#E2E8F0";
const AXIS_TICK = { fill: '#64748B', fontSize: 11 };
const COLORS = {
  primary: 'var(--module-primary)', primaryLight: 'var(--module-subtle)',
  success: '#0CBF73', successLight: 'rgba(12,191,115,0.10)',
  warning: '#F5A623', warningLight: 'rgba(245,166,35,0.10)',
  danger: '#DF1B41', dangerLight: 'rgba(223,27,65,0.10)',
  muted: '#64748B', mutedLight: 'rgba(100,116,139,0.08)',
};

// SWA-198 · de dónde salió el rango que usó la corrida.
const RANGE_SOURCE_LABEL = {
  scenario: 'rango del escenario',
  company: 'rango de Ajustes',
  catalog_default: 'rango por defecto',
  request: 'rango del request',
};

const SENSITIVITY_VARIABLES = [
  { key: 'avg_price_per_unit', label: 'Precio promedio por unidad' },
  { key: 'total_units', label: 'Número de unidades' },
  { key: 'hard_cost_per_unit', label: 'Costo duro por unidad' },
  { key: 'land_cost', label: 'Costo del terreno' },
  { key: 'loan_interest_rate_annual', label: 'Tasa de interés anual' },
  { key: 'equity_pct', label: '% Recurso propio' },
  { key: 'absorption_rate_units_per_month', label: 'Absorción (uds/mes)' },
  { key: 'construction_months', label: 'Meses de construcción' },
  { key: 'soft_cost_pct', label: 'Costos blandos (%)' },
  { key: 'contingency_pct', label: 'Contingencia (%)' },
  { key: 'enganche_pct', label: 'Enganche comprador (%)' },  // SWA-196 · lo que lee el motor
  { key: 'price_escalation_pct', label: 'Escalación de precio (%)' },
  { key: 'presale_pct', label: 'Preventa (%)' },
];

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════ */
export default function AnalyzerPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { activeProject, activeScenario, projects: ctxProjects, scenarios: ctxScenarios, setActiveProject, setActiveScenario, loadingProjects } = useProject();
  const { show_tooltips } = usePreferences();
  const { isActive } = useModules();

  const [selectedProject, setSelectedProject] = useState('');
  const [selectedScenario, setSelectedScenario] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [tornadoData, setTornadoData] = useState(null);
  const [sensitivityVar, setSensitivityVar] = useState('avg_price_per_unit');
  const [sensitivityData, setSensitivityData] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [marketValidation, setMarketValidation] = useState(null);

  // Pre-piloto · 2026-02: memoizamos las sugerencias filtradas para evitar
  // recomputo del filter/entries en cada render de este componente pesado.
  const marketSuggestionEntries = useMemo(() => (
    marketValidation?.suggestions
      ? Object.entries(marketValidation.suggestions).filter(([k]) => k !== 'avg_price_per_m2')
      : []
  ), [marketValidation]);
  const [prevMetrics, setPrevMetrics] = useState(null);
  const [changedMetrics, setChangedMetrics] = useState({});
  const [applyingMarket, setApplyingMarket] = useState(false);

  // React Query hooks
  const { data: projects = [] } = useProjects();
  const { data: scenarios = [] } = useScenarios(selectedProject);
  const runAnalysisMut = useRunAnalysis();
  const previewAnalysisMut = usePreviewAnalysis();
  // SWA-244 · la fecha de la corrida registrada NO viene en la respuesta de
  // /run (sólo run_id y duration_ms), así que se lee de la lista de corridas
  // (GET /api/analyzer/runs/{project_id}, analyzer_routes.py:570-580), que sí
  // trae `created_at`. Nunca se fabrica con Date.now() en el cliente.
  const { data: analysisRuns = [] } = useAnalysisRuns(selectedProject);
  const [pdfWarnOpen, setPdfWarnOpen] = useState(false);

  // SWA-244 · sello de estado: la respuesta se autodenuncia (`preview: true` con
  // `run_id: null`) y aquí sólo se traduce. La fecha sale del `created_at` del
  // run correspondiente en la lista de corridas.
  const isPreview = !!analysis && !analysis.run_id;
  const registeredRun = useMemo(() => {
    if (!analysis?.run_id) return null;
    return analysisRuns.find((r) => r.id === analysis.run_id) || null;
  }, [analysisRuns, analysis]);
  const lastRegisteredRun = useMemo(
    () => analysisRuns.find((r) => r.scenario_id === selectedScenario && r.status === 'completed') || null,
    [analysisRuns, selectedScenario],
  );
  const fmtRunDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };
  const sensitivityMut = useSensitivity();
  const tornadoMut = useTornado();
  const compareMut = useCompareScenarios();
  const duplicateMut = useDuplicateScenario();
  const { data: marketSuggestionsData } = useMarketSuggestions(
    selectedProject, selectedScenario, isActive('market') && !!analysis
  );

  const running = runAnalysisMut.isPending || previewAnalysisMut.isPending;
  const tornadoLoading = tornadoMut.isPending;
  const sensitivityLoading = sensitivityMut.isPending;
  const compareLoading = compareMut.isPending;

  // Set market validation from query
  useEffect(() => {
    if (marketSuggestionsData?.has_suggestions) setMarketValidation(marketSuggestionsData);
  }, [marketSuggestionsData]);

  // Read URL query params (from wizard "Ejecutar" button or direct links)
  useEffect(() => {
    const qProject = searchParams.get('project');
    const qScenario = searchParams.get('scenario');
    if (qProject && !selectedProject) setSelectedProject(qProject);
    if (qScenario && !selectedScenario) setSelectedScenario(qScenario);
  }, []);

  // Sync with global context
  useEffect(() => {
    if (activeProject?.id && !selectedProject) {
      setSelectedProject(activeProject.id);
    }
  }, [activeProject]);

  useEffect(() => {
    if (activeScenario?.id && selectedProject && !selectedScenario) {
      setSelectedScenario(activeScenario.id);
    }
  }, [activeScenario?.id, selectedProject]);

  // Auto-select most recent scenario when project is selected but no scenario in context
  useEffect(() => {
    if (selectedProject && !selectedScenario && !activeScenario?.id && scenarios.length > 0) {
      const sorted = [...scenarios].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      if (sorted[0]) {
        setSelectedScenario(sorted[0].id);
        setActiveScenario(sorted[0]);
      }
    }
  }, [selectedProject, scenarios, selectedScenario, activeScenario]);

  // Auto-run analysis when both project and scenario are selected
  // SWA-240 · el montaje PREVISUALIZA: calcula sin registrar corrida. Registrar
  // aquí hacía que el PDF ejecutivo retratara la corrida del montaje en vez de
  // la que el usuario lanzó.
  useEffect(() => {
    if (selectedProject && selectedScenario && !analysis && !running) {
      handlePreviewAnalysis();
    }
  }, [selectedProject, selectedScenario]);

  const handlePreviewAnalysis = async () => {
    if (!selectedProject || !selectedScenario) return;
    try {
      const result = await previewAnalysisMut.mutateAsync({ scenario_id: selectedScenario, project_id: selectedProject });
      setAnalysis(result);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Error en el análisis'));
    }
  };

  const handleRunAnalysis = async () => {
    if (!selectedProject || !selectedScenario) return;
    try {
      const result = await runAnalysisMut.mutateAsync({ scenario_id: selectedScenario, project_id: selectedProject });
      setAnalysis(result);
      toast.success(`Análisis ejecutado en ${result.duration_ms}ms`);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Error en el análisis'));
    }
  };

  const handleApplyMarketSuggestion = async (key, value) => {
    if (!selectedProject || !selectedScenario) return;
    setApplyingMarket(true);
    const oldMetrics = analysis?.metrics ? { ...analysis.metrics } : null;
    setPrevMetrics(oldMetrics);

    try {
      await scenariosAPI.updateAssumptions(selectedProject, selectedScenario, [{ key, value }]);
      const result = await runAnalysisMut.mutateAsync({ scenario_id: selectedScenario, project_id: selectedProject });
      setAnalysis(result);
      if (oldMetrics && result.metrics) {
        const deltas = {};
        for (const mk of ['irr_annual', 'npv', 'total_profit', 'peak_equity', 'peak_equity_required', 'peak_total_funding', 'margen_neto']) {
          const prev = oldMetrics[mk];
          const curr = result.metrics[mk];
          if (prev != null && curr != null && Math.abs(curr - prev) > 0.01) {
            deltas[mk] = { prev, curr, delta: curr - prev };
          }
        }
        setChangedMetrics(deltas);
        setTimeout(() => setChangedMetrics({}), 5000);
      }
      toast.success('Análisis actualizado con datos de mercado');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Error al aplicar sugerencia'));
    }
    setApplyingMarket(false);
  };

  const handleRunTornado = async () => {
    if (!selectedProject || !selectedScenario) return;
    try {
      const result = await tornadoMut.mutateAsync({ scenario_id: selectedScenario, project_id: selectedProject });
      setTornadoData(result);
    } catch {
      toast.error('Error en análisis tornado');
    }
  };

  const handleRunSensitivity = async () => {
    if (!selectedProject || !selectedScenario || !sensitivityVar) return;
    try {
      const result = await sensitivityMut.mutateAsync({
        scenario_id: selectedScenario, project_id: selectedProject,
        variable: sensitivityVar, steps: 9,
      });
      setSensitivityData(result);
    } catch {
      toast.error('Error en análisis de sensibilidad');
    }
  };

  const handleCompare = async () => {
    if (!selectedProject || compareIds.length < 2) return;
    try {
      const result = await compareMut.mutateAsync({ scenario_ids: compareIds, project_id: selectedProject });
      setCompareData(result);
    } catch {
      toast.error('Error en comparación de escenarios');
    }
  };

  const handleDuplicate = async () => {
    if (!selectedProject || !selectedScenario) return;
    try {
      const res = await duplicateMut.mutateAsync({ projectId: selectedProject, scenarioId: selectedScenario });
      toast.success(`Escenario "${res.name}" duplicado`);
    } catch { toast.error('Error al duplicar'); }
  };

  const handleExportExcel = async (reportType) => {
    if (!selectedProject || !selectedScenario) { toast.error('Selecciona proyecto y escenario'); return; }
    toast.info('Generando Excel...');
    try {
      const genRes = await reportsAPI.generate({ report_type: reportType, project_id: selectedProject, scenario_id: selectedScenario });
      const reportId = genRes.data?.id;
      if (!reportId) { toast.error('Error generando'); return; }
      const response = await fetch(`/api/reports/${reportId}/excel`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      if (!response.ok) { toast.error('Error descargando Excel'); return; }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${reportType}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Excel descargado');
    } catch { toast.error('Error'); }
  };

  const handleExportPDF = async () => {
    if (!selectedProject || !selectedScenario) { toast.error('Selecciona proyecto y escenario'); return; }
    // SWA-244 · si en pantalla hay una previsualización, el PDF retratará otra
    // corrida (o ninguna). Se avisa, no se impide: el usuario decide.
    if (isPreview) { setPdfWarnOpen(true); return; }
    await downloadPDF();
  };

  const downloadPDF = async () => {
    toast.info('Generando PDF ejecutivo...');
    try {
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/analyzer/export-pdf/${selectedProject}/${selectedScenario}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Error descargando PDF');
        return;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const cd = response.headers.get('Content-Disposition') || '';
      const nameMatch = cd.match(/filename="([^"]+)"/);
      const filename = nameMatch ? nameMatch[1] : 'reporte_ejecutivo.pdf';
      const a = document.createElement('a'); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('PDF ejecutivo descargado');
    } catch { toast.error('Error generando PDF'); }
  };

  const metrics = analysis?.metrics || {};
  const rm = getReturnMetric(metrics); // SWA-139 · una sola fuente para las dos tarjetas de retorno
  const schedule = analysis?.monthly_schedule || [];
  const costSummary = analysis?.cost_summary || {};
  const alerts = analysis?.alerts || [];

  // Chart data
  const chartData = useMemo(() => {
    // Desglose de liability_balance por priority (lectura DIRECTA del motor, sin recalcular)
    // La fuente de verdad es result.liability_schedule[].per_liability[].balance_end + priority.
    const liabSched = analysis?.liability_schedule || [];
    const balanceByMonth = {}; // {month: {senior, subordinated}}
    liabSched.forEach((m) => {
      const per = m.per_liability || [];
      const senior = per.filter(p => p.priority === 'senior_to_equity').reduce((s, p) => s + (p.balance_end || 0), 0);
      const sub = per.filter(p => p.priority === 'subordinated').reduce((s, p) => s + (p.balance_end || 0), 0);
      balanceByMonth[m.month] = { senior, subordinated: sub };
    });

    return schedule.map((m) => {
      const liab = balanceByMonth[m.month] || { senior: 0, subordinated: 0 };
      return {
        name: `M${m.month}`,
        month: m.month,
        ingresos: m.revenue_effective || 0,
        egresos: -(m.total_cost || 0),
        flujo_neto: m.net_cashflow || 0,
        flujo_acumulado: m.cumulative_cashflow || 0,
        saldo_credito: (m.loan_balance || 0) + (m.liability_balance || 0),
        liab_senior: liab.senior,
        liab_subordinated: liab.subordinated,
        liab_total: (liab.senior + liab.subordinated),
        equity_acum: m.cumulative_equity || 0,
        operativo: m.operating_cashflow || 0,
        enganche: (m.enganche || 0) + (m.apartado || 0),
        parcialidades: m.parcialidades || 0,
        escrituracion: (m.escrituracion || 0) + (m.pago_entrega || 0),
        land: m.land_cost || 0,
        hard: m.hard_cost || 0,
        soft: m.soft_cost || 0,
        indirect: m.indirect_cost || 0,
      };
    });
  }, [schedule, analysis]);

  const tickInterval = Math.max(0, Math.floor(chartData.length / 12));
  const currentScenarioObj = scenarios.find(s => s.id === selectedScenario);

  // Show guided state if no projects at all
  if (!loadingProjects && projects.length === 0) {
    return <RequiresProject moduleName="Financial Intelligence"><div /></RequiresProject>;
  }

  return (
    <div className="h-full overflow-y-auto" data-testid="financial-analyzer-page">
      <div className="px-6 lg:px-8 py-6 max-w-[1600px]">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-display font-semibold tracking-tight">Financial Intelligence</h1>
          <p className="text-sm text-muted-foreground mt-1">Motor de análisis financiero inmobiliario profesional</p>
        </div>

        {/* Controls */}
        <Card className="rounded-xl border-border/50 shadow-sm mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground font-medium">Proyecto</p>
                <Select value={selectedProject} onValueChange={(v) => { setSelectedProject(v); setSelectedScenario(''); setAnalysis(null); const p = projects.find(x => x.id === v); if (p) setActiveProject(p); }}>
                  <SelectTrigger className="w-[220px]" data-testid="project-selector"><SelectValue placeholder="Seleccionar proyecto" /></SelectTrigger>
                  <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground font-medium">Escenario</p>
                <Select value={selectedScenario} onValueChange={(v) => { setSelectedScenario(v); setAnalysis(null); const s = scenarios.find(x => x.id === v); if (s) setActiveScenario(s); }}>
                  <SelectTrigger className="w-[220px]" data-testid="scenario-selector"><SelectValue placeholder="Seleccionar escenario" /></SelectTrigger>
                  <SelectContent>{scenarios.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {selectedScenario && selectedProject && (
                <Button variant="outline" size="sm" onClick={() => navigate(`/analyzer/wizard/${selectedProject}/${selectedScenario}`)} data-testid="open-wizard-btn">
                  <Settings2 className="mr-2 h-4 w-4" /> Editar Inputs
                </Button>
              )}
              {selectedScenario && selectedProject && (
                <Button
                  size="sm"
                  onClick={handleRunAnalysis}
                  disabled={running}
                  data-testid="register-run-btn"
                >
                  <Play className="mr-2 h-4 w-4" /> Registrar corrida
                </Button>
              )}
              {analysis && selectedScenario && selectedProject && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportPDF}
                  data-testid="export-pdf-btn"
                  style={{ borderColor: '#6366F1', color: '#4F46E5' }}
                >
                  <FileText className="mr-2 h-4 w-4" /> Exportar PDF
                </Button>
              )}
              {running && (
                <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
                  <Loader2 className="h-4 w-4 animate-spin" style={{ color: 'var(--accent-primary)' }} /> Analizando...
                </div>
              )}
              {analysis && (
                <div className="flex items-center gap-2 ml-auto">
                  {/* SWA-244 · sello: la pantalla dice cuál de las dos cosas muestra */}
                  <Badge
                    variant="outline"
                    className={`text-xs gap-1 ${isPreview && lastRegisteredRun ? 'flex-col items-start' : ''}`}
                    data-testid="run-state-seal"
                    style={isPreview
                      ? { borderColor: '#FDBA74', color: '#9A3412', background: '#FFF7ED' }
                      : { borderColor: '#86EFAC', color: '#166534', background: '#F0FDF4' }}
                  >
                    {isPreview
                      ? 'Previsualización · sin registrar'
                      : `Corrida registrada el ${fmtRunDate(registeredRun?.created_at) || '—'}`}
                    {/* SWA-245 · la otra mitad: existe una corrida registrada, y de cuándo */}
                    {isPreview && lastRegisteredRun && (
                      <span className="block text-[10px] opacity-80" data-testid="run-state-last">
                        {`última registrada: ${fmtRunDate(lastRegisteredRun.created_at)}`}
                      </span>
                    )}
                  </Badge>
                  <Badge variant="outline" className="text-xs gap-1">
                    v{analysis.engine_version} — {analysis.duration_ms}ms
                  </Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* SWA-244 · aviso al exportar sobre una previsualización. Avisa, no impide. */}
        <AlertDialog open={pdfWarnOpen} onOpenChange={setPdfWarnOpen}>
          <AlertDialogContent data-testid="pdf-preview-warning">
            <AlertDialogHeader>
              <AlertDialogTitle>Lo que ves no es la corrida que se exportará</AlertDialogTitle>
              <AlertDialogDescription>
                {lastRegisteredRun
                  ? `Los números en pantalla son una previsualización sin registrar. El PDF se construye sobre la última corrida registrada de este escenario, del ${fmtRunDate(lastRegisteredRun.created_at)}, así que el documento puede no corresponder a lo que estás viendo.`
                  : 'Los números en pantalla son una previsualización sin registrar y este escenario no tiene ninguna corrida registrada, así que no hay nada que el PDF pueda retratar.'}
                {' '}Puedes registrar la corrida actual antes de exportar, o exportar tal como está.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="pdf-warning-cancel">Cancelar</AlertDialogCancel>
              <AlertDialogCancel
                onClick={async () => { setPdfWarnOpen(false); await downloadPDF(); }}
                data-testid="pdf-warning-export-anyway"
              >
                Exportar así
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => { setPdfWarnOpen(false); await handleRunAnalysis(); await downloadPDF(); }}
                data-testid="pdf-warning-register-first"
              >
                Registrar y exportar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Results */}
        {!analysis ? (
          <Card className="rounded-xl border-border/50 shadow-sm">
            <CardContent className="py-20 text-center">
              <BarChart3 className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--primary-hex)', opacity: 0.3 }} />
              <h3 className="text-lg font-display font-semibold mb-2">Financial Intelligence</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Selecciona un proyecto y escenario, luego ejecuta el análisis para obtener métricas financieras, flujos de caja y visualizaciones.
              </p>
            </CardContent>
          </Card>
        ) : (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-5">
                <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
                <TabsTrigger value="corrida" data-testid="tab-corrida">Corrida Financiera</TabsTrigger>
                <TabsTrigger value="cashflow" data-testid="tab-cashflow">Flujo Mensual</TabsTrigger>
                <TabsTrigger value="charts" data-testid="tab-charts">Gráficas</TabsTrigger>
                <TabsTrigger value="scenarios" data-testid="tab-scenarios">Escenarios</TabsTrigger>
                <TabsTrigger value="sensitivity" data-testid="tab-sensitivity">Sensibilidad</TabsTrigger>
                <TabsTrigger value="stress" data-testid="tab-stress">Stress Test</TabsTrigger>
              </TabsList>

              {/* KPI + Summary bars — hidden on Dashboard and Stress tabs to avoid duplicates */}
              {activeTab !== 'dashboard' && activeTab !== 'stress' && (<>
                <div className="grid grid-cols-4 md:grid-cols-9 gap-2 mb-2" data-testid="kpi-bar">
                  {[
                    [rm.key === 'irr_annual' ? 'TIR inv.' : rm.label, rm.value, rm.key === 'moic' ? 'x' : 'pct', true],
                    ['VAN inv.', metrics.npv, 'cur', true],
                    ['ROI s/ Capital', metrics.roi, 'pct', false],
                    ['MOIC', metrics.moic, 'x', false],
                    ['Payback', metrics.payback_month, 'mo', false],
                    ['Capital propio máx.', metrics.peak_equity_required, 'cur', false],
                    ['Financ. máx. total', metrics.peak_total_funding, 'cur', false],
                    ['Peak Debt', metrics.peak_debt, 'cur', false],
                    ['Margen Neto', metrics.margen_neto, 'pct', false],
                  ].map(([label, val, fmt, highlight]) => (
                    <div key={label} className="rounded-lg p-2.5 text-center" style={{ background: highlight ? 'var(--module-subtle)' : 'var(--bg-hover)', border: highlight ? '1px solid var(--module-light)' : '1px solid transparent' }}>
                      <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground mb-1" style={{ letterSpacing: '0.5px' }}>{label}</p>
                      <p className="text-sm font-display font-bold tabular-nums" style={highlight ? { color: 'var(--module-primary)' } : {}}>
                        {val == null ? '—' : fmt === 'pct' ? formatPercent(val) : fmt === 'cur' ? formatCurrency(val) : fmt === 'x' ? `${formatNumber(val,2)}x` : fmt === 'mo' ? `Mes ${val}` : val}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mb-5">
                  {[
                    ['Ingresos', metrics.total_revenue, 'var(--success)'],
                    ['Costos', metrics.total_cost, 'var(--danger)'],
                    ['Utilidad', metrics.total_profit, metrics.total_profit >= 0 ? 'var(--success)' : 'var(--danger)'],
                    ['M. Bruto', metrics.margen_bruto, 'var(--text-secondary)'],
                    ['P.E. Ingresos', metrics.breakeven_revenue, 'var(--text-secondary)'],
                    ['P.E. Unidades', metrics.breakeven_units, 'var(--text-secondary)'],
                    ['Utilidad/Ud', metrics.profit_per_unit, 'var(--text-secondary)'],
                    ['Duración', metrics.project_duration_months, 'var(--text-secondary)'],
                  ].map(([label, val, color]) => (
                    <div key={label} className="rounded-lg p-2.5 text-center bg-card" style={{ border: '1px solid var(--border-default)' }}>
                      <p className="text-[9px] font-semibold uppercase tracking-wide text-muted-foreground mb-1" style={{ letterSpacing: '0.5px' }}>{label}</p>
                      <p className="text-sm font-display font-bold tabular-nums" style={{ color: color || 'var(--text-primary)' }}>
                        {val == null ? '—'
                          : label === 'Duración' ? `${val}m`
                          : label === 'P.E. Unidades' ? `${formatNumber(val,0)} uds`
                          : label === 'M. Bruto' ? formatPercent(val)
                          : formatCurrency(val)}
                      </p>
                    </div>
                  ))}
                </div>
              </>)}

              {/* ═══ DASHBOARD TAB ═══ */}
              <TabsContent value="dashboard" className="space-y-6">
                {/* Alerts panel */}
                {alerts.length > 0 && (
                  <div className="space-y-2.5" data-testid="alerts-panel">
                    {alerts.map((alert, i) => {
                      const styles = {
                        critical: { border: 'var(--danger)', bg: 'var(--danger-soft)', icon: '!', iconBg: 'var(--danger)', text: '#7F1D1D' },
                        warning: { border: 'var(--warning-color)', bg: 'var(--warning-soft)', icon: '!', iconBg: 'var(--warning-color)', text: '#78350F' },
                        info: { border: 'var(--info)', bg: 'var(--info-soft)', icon: 'i', iconBg: 'var(--info)', text: '#1E3A5F' },
                      }[alert.severity] || { border: 'var(--text-muted)', bg: 'var(--bg-hover)', icon: '?', iconBg: 'var(--text-muted)', text: 'var(--text-primary)' };

                      return (
                        <div key={i} className="flex gap-3 p-3.5 rounded-xl border" data-testid={`alert-${alert.severity}-${i}`}
                          style={{ borderColor: styles.border + '40', background: styles.bg }}>
                          <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-white text-xs font-bold"
                            style={{ background: styles.iconBg }}>
                            {styles.icon}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-sm font-display font-semibold" style={{ color: styles.text }}>{alert.title}</span>
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0" style={{ borderColor: styles.border + '60', color: styles.text }}>
                                {alert.category}
                              </Badge>
                            </div>
                            <p className="text-xs leading-relaxed" style={{ color: styles.text + 'CC' }}>{alert.description}</p>
                            <p className="text-xs mt-1.5 font-medium" style={{ color: styles.text + 'BB' }}>
                              <span className="opacity-60">Recomendación:</span> {alert.recommendation}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Overrun absorption banner (G5.1) — only when expected_overrun_pct > 0 */}
                {analysis?.overrun_report?.status && analysis.overrun_report.status !== 'none' && (() => {
                  const r = analysis.overrun_report;
                  const isAbsorbed = r.status === 'absorbed';
                  const overrunPct = (r.expected_overrun_pct * 100).toFixed(1);
                  const contPct = (r.contingency_pct * 100).toFixed(1);
                  return (
                    <EngineBanner
                      severity={isAbsorbed ? 'success' : 'warning'}
                      title={`Sobrecosto esperado: ${isAbsorbed ? 'absorbido por contingencia' : 'excede la contingencia'}`}
                      tag="G5.1"
                      testid="overrun-banner"
                    >
                      <span data-testid="overrun-explanation">
                        Sobrecosto {overrunPct}% {isAbsorbed ? '≤' : '>'} contingencia {contPct}%.
                        {isAbsorbed ? (
                          <> El colchón cubre el riesgo, <strong>impacto neto $0</strong>.</>
                        ) : (
                          <> Exceso: <strong data-testid="overrun-net-impact">
                            {formatCurrency(r.net_impact_amount)}
                          </strong> adicional al costo presupuestado.</>
                        )}
                      </span>
                    </EngineBanner>
                  );
                })()}

                {/* Truncation banner (Check 7) — only when total_truncated > 0 */}
                {analysis?.truncation_report?.total_truncated > 0 && (() => {
                  const tr = analysis.truncation_report;
                  const cats = [
                    { key: 'ingreso', label: 'Ingreso', val: tr.collection_effective_truncated || 0 },
                    { key: 'costo', label: 'Costo', val: tr.cost_truncated || 0 },
                    { key: 'deuda', label: 'Deuda',
                      val: (tr.debt_principal_truncated || 0) + (tr.debt_interest_truncated || 0) },
                  ].filter(c => c.val > 0.01);
                  return (
                    <EngineBanner
                      severity="warning"
                      title="Flujos fuera de la ventana de análisis"
                      tag="Check 7"
                      amount={formatCurrency(tr.total_truncated)}
                      testid="truncation-banner"
                    >
                      <span data-testid="truncation-total" className="sr-only">
                        {formatCurrency(tr.total_truncated)}
                      </span>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-2">
                        {cats.map(c => (
                          <div key={c.key} className="rounded-md px-2.5 py-1.5 bg-white/60"
                            data-testid={`truncation-cat-${c.key}`}>
                            <p className="text-[9px] uppercase tracking-wide font-semibold" style={{ color: '#78350F99' }}>
                              {c.label}
                            </p>
                            <p className="text-xs font-bold tabular-nums" style={{ color: '#78350F' }}>
                              {formatCurrency(c.val)}
                            </p>
                          </div>
                        ))}
                        <div className="rounded-md px-2.5 py-1.5 bg-white/60">
                          <p className="text-[9px] uppercase tracking-wide font-semibold" style={{ color: '#78350F99' }}>
                            Eventos
                          </p>
                          <p className="text-xs font-bold tabular-nums" style={{ color: '#78350F' }}>
                            {tr.events_truncated || 0}
                            {tr.max_month_with_truncated_event ? ` · mes máx ${tr.max_month_with_truncated_event}` : ''}
                          </p>
                        </div>
                        <div className="rounded-md px-2.5 py-1.5 bg-white/60">
                          <p className="text-[9px] uppercase tracking-wide font-semibold" style={{ color: '#78350F99' }}>
                            Horizonte
                          </p>
                          <p className="text-xs font-bold tabular-nums" style={{ color: '#78350F' }}>
                            {tr.horizon_months || 0} meses
                          </p>
                        </div>
                      </div>
                      <p className="text-xs font-medium">
                        <span className="opacity-60">Acción sugerida:</span>{' '}
                        Extiende el horizon o ajusta la curva de entrega/cobranza para capturar estos flujos dentro de la ventana de análisis.
                      </p>
                    </EngineBanner>
                  );
                })()}

                {/* SWA-349 · dos bloques: proyecto e inversionista */}
                <div className="space-y-4" data-testid="kpi-blocks">
                  <div data-testid="kpi-block-project">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Proyecto · sin crédito</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <KPICard label="VAN del proyecto" value={metrics.npv_project} format="currency" icon={DollarSign} highlight subtitle={`Tasa ${formatPercent(metrics.discount_rate_used)} · Mes 1 = hoy`} showTooltips={show_tooltips} delta={changedMetrics.npv_project} />
                      <KPICard label="TIR del proyecto" value={metrics.is_irr_project_reliable === false ? null : metrics.irr_project} format="percent" icon={TrendingUp} subtitle={metrics.is_irr_project_reliable === false ? (metrics.irr_project_note || 'No confiable') : 'Terreno, obra y venta'} showTooltips={show_tooltips} delta={changedMetrics.irr_project} />
                    </div>
                  </div>
                  <div data-testid="kpi-block-investor">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Inversionista · aportaciones y distribuciones</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <KPICard label="VAN del inversionista" value={metrics.npv} format="currency" icon={DollarSign} highlight subtitle={`Tasa ${formatPercent(metrics.equity_required_return_used)} · Mes 1 = hoy`} showTooltips={show_tooltips} delta={changedMetrics.npv} />
                      <KPICard label="TIR del inversionista" value={rm.reliable ? metrics.irr_annual : null} format="percent" icon={TrendingUp} highlight subtitle={rm.reliable ? 'Anual' : rm.note} showTooltips={show_tooltips} delta={changedMetrics.irr_annual} />
                      <KPICard label="MOIC" value={metrics.moic} format="multiple" icon={Target} subtitle="Distribuciones ÷ aportaciones" showTooltips={show_tooltips} delta={changedMetrics.moic} />
                      <KPICard label="MIRR" value={metrics.mirr} format="percent" icon={TrendingUp} subtitle={`Financia a ${formatPercent(metrics.equity_required_return_used)} · reinvierte a ${formatPercent(metrics.mirr_reinvest_rate)}`} showTooltips={show_tooltips} delta={changedMetrics.mirr} />
                    </div>
                  </div>
                </div>

                {/* KPI Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-grid">
                  <KPICard label="ROI" value={metrics.roi} format="percent" icon={ArrowUpRight} subtitle="Retorno sobre inversión" showTooltips={show_tooltips} />
                  <KPICard label="Payback" value={metrics.payback_month} format="month" icon={Calendar} subtitle="Mes de recuperación" showTooltips={show_tooltips} />
                  <KPICard label="Capital propio máximo" value={metrics.peak_equity_required} format="currency" icon={Wallet} subtitle="Máximo del equity acumulado" showTooltips={show_tooltips} delta={changedMetrics.peak_equity_required} />
                  <KPICard label="Peak Debt" value={metrics.peak_debt} format="currency" icon={AlertTriangle} subtitle="Saldo máximo de deuda" showTooltips={show_tooltips} />
                  <KPICard label="Mes Crítico Liquidez" value={metrics.critical_liquidity_month} format="month" icon={AlertTriangle} subtitle="Mes con menor flujo acum." showTooltips={show_tooltips} />
                </div>

                {/* SWA-125 · Estructura de capital resultante (fuentes y usos, convención firmada 3-nov) */}
                {metrics.funding_structure && metrics.funding_structure.uses_total > 0 && (
                  <div className="p-4 rounded-lg bg-muted/30 border border-border/50" data-testid="funding-structure">
                    <div className="flex items-baseline justify-between mb-2">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Estructura de capital resultante</p>
                      <p className="text-[10px] text-muted-foreground tabular-nums">Usos {formatCurrency(metrics.funding_structure.uses_total)}</p>
                    </div>
                    <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted mb-3">
                      <div style={{ width: `${Math.max(0, metrics.funding_structure.equity_pct || 0)}%`, background: 'var(--accent-primary, #2563EB)' }} />
                      <div style={{ width: `${Math.max(0, metrics.funding_structure.credit_pct || 0)}%`, background: '#64748B' }} />
                      <div style={{ width: `${Math.max(0, metrics.funding_structure.collections_pct || 0)}%`, background: '#94A3B8' }} />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        ['Capital propio', metrics.funding_structure.equity, metrics.funding_structure.equity_pct],
                        ['Crédito dispuesto', metrics.funding_structure.credit, metrics.funding_structure.credit_pct],
                        ['Cobranza aplicada', metrics.funding_structure.collections, metrics.funding_structure.collections_pct],
                      ].map(([l, v, p]) => (
                        <div key={l}>
                          <p className="text-[10px] text-muted-foreground">{l}</p>
                          <p className="text-sm font-semibold tabular-nums">{formatCurrency(v)}</p>
                          <p className="text-[10px] text-muted-foreground tabular-nums">{p != null ? `${Number(p).toFixed(1)} % de los usos` : '—'}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-2">Usos = costo total + intereses y comisiones del crédito + servicio de pasivos. El repago del crédito no es uso: lo pagan las ventas.</p>
                  </div>
                )}

                {/* Secondary KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  {[
                    { label: 'Financiamiento máximo total', value: formatCurrency(metrics.peak_total_funding),
                      sub: metrics.peak_total_funding != null ? `capital ${formatCurrency(metrics.peak_total_funding_equity)} + deuda ${formatCurrency(metrics.peak_total_funding_debt)}` : null },
                    { label: 'Margen Bruto', value: formatPercent(metrics.margen_bruto) },
                    { label: 'Margen Neto', value: formatPercent(metrics.margen_neto) },
                    { label: 'P.E. Ingresos', value: formatCurrency(metrics.breakeven_revenue) },
                    { label: 'P.E. Unidades', value: metrics.breakeven_units != null ? `${formatNumber(metrics.breakeven_units, 0)} uds` : '—' },
                    { label: 'Utilidad / Unidad', value: formatCurrency(metrics.profit_per_unit) },
                  ].map(kpi => {
                    const tip = KPI_TOOLTIPS[kpi.label];
                    return (
                      <div key={kpi.label} className="p-3 rounded-lg bg-muted/30 border border-border/50">
                        <div className="flex items-center gap-1">
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{kpi.label}</p>
                          {tip && (
                            <TooltipProvider delayDuration={200}>
                              <UITooltip>
                                <TooltipTrigger asChild>
                                  <Info className="w-2.5 h-2.5 text-muted-foreground/40 hover:text-muted-foreground cursor-help shrink-0" />
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-[300px] p-3" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-default)', color: 'var(--text-primary)' }}>
                                  <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{kpi.label}</p>
                                  <p className="text-[11px] leading-relaxed mb-1.5" style={{ color: 'var(--text-secondary)' }}>{tip.def}</p>
                                  <p className="text-[10px] leading-relaxed" style={{ color: 'var(--text-muted)' }}>{tip.why}</p>
                                </TooltipContent>
                              </UITooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        <p className="text-lg font-display font-semibold tabular-nums">{kpi.value}</p>
                        {kpi.sub && <p className="text-[10px] text-muted-foreground tabular-nums">{kpi.sub}</p>}
                      </div>
                    );
                  })}
                </div>

                {/* Summary cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="rounded-xl border-border/50 shadow-sm">
                    <CardContent className="p-5">
                      <h4 className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-3">Ingresos</h4>
                      <p className="text-2xl font-display font-semibold tabular-nums" style={{color:'var(--success)'}}>{formatCurrency(metrics.total_revenue)}</p>
                      <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                        <div className="flex justify-between"><span>Unidades</span><span className="tabular-nums">{analysis?.revenue_summary?.total_units}</span></div>
                        <div className="flex justify-between"><span>Precio promedio</span><span className="tabular-nums">{formatCurrency(analysis?.revenue_summary?.avg_price_per_unit)}</span></div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="rounded-xl border-border/50 shadow-sm">
                    <CardContent className="p-5">
                      <h4 className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-3">Costos</h4>
                      <p className="text-2xl font-display font-semibold tabular-nums" style={{color:'var(--danger)'}}>{formatCurrency(metrics.total_cost)}</p>
                      <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                        <div className="flex justify-between"><span>Terreno</span><span className="tabular-nums">{formatCurrency(costSummary.land_cost)}</span></div>
                        <div className="flex justify-between"><span>Preoperativos</span><span className="tabular-nums">{formatCurrency(costSummary.preoperative_costs)}</span></div>
                        <div className="flex justify-between"><span>Costos duros</span><span className="tabular-nums">{formatCurrency(costSummary.hard_costs)}</span></div>
                        <div className="flex justify-between"><span>Indirectos</span><span className="tabular-nums">{formatCurrency(costSummary.indirect_costs)}</span></div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="rounded-xl border-border/50 shadow-sm">
                    <CardContent className="p-5">
                      <h4 className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-3">Utilidad</h4>
                      <p className="text-2xl font-display font-semibold tabular-nums" style={{color: metrics.total_profit >= 0 ? 'var(--success)' : 'var(--danger)'}}>{formatCurrency(metrics.total_profit)}</p>
                      <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                        <div className="flex justify-between"><span>Intereses pagados</span><span className="tabular-nums">{formatCurrency(metrics.total_interest)}</span></div>
                        <div className="flex justify-between"><span>Equity aportado</span><span className="tabular-nums">{formatCurrency(metrics.total_equity)}</span></div>
                        <div className="flex justify-between"><span>Duración</span><span className="tabular-nums">{metrics.project_duration_months} meses</span></div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Market Validation Panel */}
                {marketValidation?.has_suggestions ? (
                  <Card className="rounded-xl border-border/50 shadow-sm" data-testid="market-validation-panel">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <TrendingUp className="w-4 h-4" style={{ color: 'var(--market-primary)' }} />
                        <h3 className="text-xs font-display font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Validación de mercado</h3>
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full ml-auto" style={{ background: 'var(--market-light)', color: 'var(--market-primary)' }}>
                          {marketValidation.comparables_count} comparables
                        </span>
                      </div>
                      <div className="space-y-3">
                        {marketSuggestionEntries.map(([key, sug]) => {
                          const diff = sug.diff_pct;
                          const hasRisk = diff != null && Math.abs(diff) > 15;
                          const isAligned = diff != null && Math.abs(diff) <= 5;
                          const isCurrency = sug.label?.includes('Precio');
                          const fmtV = (v) => isCurrency ? `$${Number(v).toLocaleString('es-MX', { maximumFractionDigits: 0 })}` : Number(v).toLocaleString('es-MX', { maximumFractionDigits: 1 });
                          return (
                            <div key={key} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: hasRisk ? 'var(--warning-soft)' : 'var(--bg-primary)', border: `1px solid ${hasRisk ? 'rgba(245,166,35,0.15)' : 'var(--border-subtle)'}` }}>
                              <div className="flex-1 min-w-0">
                                <div className="text-[10px] font-medium mb-0.5" style={{ color: 'var(--text-muted)' }}>{sug.label}</div>
                                <div className="flex items-center gap-3">
                                  <div>
                                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Actual: </span>
                                    <span className="text-sm font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{sug.current != null ? fmtV(sug.current) : '—'}</span>
                                  </div>
                                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>→</span>
                                  <div>
                                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Mercado: </span>
                                    <span className="text-sm font-semibold tabular-nums" style={{ color: 'var(--market-primary)' }}>{fmtV(sug.suggested)}</span>
                                  </div>
                                  {diff != null && (
                                    <span className="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded-full" style={{
                                      background: isAligned ? 'var(--success-soft)' : hasRisk ? 'var(--warning-soft)' : 'var(--info-soft)',
                                      color: isAligned ? 'var(--success)' : hasRisk ? 'var(--warning-color)' : 'var(--info)',
                                    }}>
                                      {diff > 0 ? '+' : ''}{diff.toFixed(1)}%
                                    </span>
                                  )}
                                </div>
                              </div>
                              {!isAligned && (
                                <Button size="sm" variant="outline" className="shrink-0 text-[10px] h-7 gap-1" disabled={applyingMarket}
                                  style={{ borderColor: 'var(--market-primary)', color: 'var(--market-primary)' }}
                                  onClick={() => handleApplyMarketSuggestion(key, sug.suggested)}
                                  data-testid={`apply-market-${key}`}>
                                  {applyingMarket ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Aplicar'}
                                </Button>
                              )}
                              {isAligned && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full shrink-0" style={{ background: 'var(--success-soft)', color: 'var(--success)' }}>Alineado</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-[9px] mt-3 text-center" style={{ color: 'var(--text-muted)' }}>{marketValidation.source}</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    <UpsellCard fromModule="financial" toModule="market" />
                    <UpsellCard fromModule="financial" toModule="construction" />
                  </div>
                )}

                {/* Decision Engine */}
                <DecisionEngine compact />

                {/* Mini charts on dashboard */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <Card className="rounded-xl border-border/50 shadow-sm">
                    <CardContent className="p-5">
                      <h4 className="text-sm font-display font-semibold mb-4">Flujo Acumulado</h4>
                      <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} opacity={0.5} />
                          <XAxis dataKey="name" tick={AXIS_TICK} interval={tickInterval} />
                          <YAxis tick={AXIS_TICK} tickFormatter={(v) => `${(v/1e6).toFixed(0)}M`} />
                          <Tooltip content={<CustomTooltip />} />
                          <ReferenceLine y={0} stroke={CHART_GRID} strokeWidth={1.5} />
                          <Area type="monotone" dataKey="flujo_acumulado" name="Flujo acumulado" stroke={COLORS.primary} fill={COLORS.primaryLight} strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                  <Card className="rounded-xl border-border/50 shadow-sm">
                    <CardContent className="p-5">
                      <h4 className="text-sm font-display font-semibold mb-4">Saldo de Crédito</h4>
                      <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} opacity={0.5} />
                          <XAxis dataKey="name" tick={AXIS_TICK} interval={tickInterval} />
                          <YAxis tick={AXIS_TICK} tickFormatter={(v) => `${(v/1e6).toFixed(0)}M`} />
                          <Tooltip content={<CustomTooltip />} />
                          <Area type="monotone" dataKey="saldo_credito" name="Saldo crédito" stroke={COLORS.warning} fill={COLORS.warningLight} strokeWidth={2} />
                          <Area type="monotone" dataKey="equity_acum" name="Equity acumulado" stroke={COLORS.success} fill={COLORS.successLight} strokeWidth={1.5} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                {/* ── Apalancamiento / Solver ── */}
                <div className="mt-6">
                  <SolverPanel
                    projectId={selectedProject}
                    scenarioId={selectedScenario}
                    analysis={analysis}
                  />
                </div>
              </TabsContent>

              {/* ═══ CASHFLOW TAB ═══ */}
              <TabsContent value="cashflow">
                <div className="space-y-3">
                  <div className="flex justify-end">
                    <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => handleExportExcel('monthly_cashflow')}>
                      <Download className="h-3.5 w-3.5" /> Exportar Excel
                    </Button>
                  </div>
                  <div className="border rounded-xl border-border/50" style={{ maxHeight: '600px', overflow: 'auto', background: '#FFFFFF' }}>
                  <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
                    <thead className="sticky top-0" style={{ zIndex: 20 }}>
                      <tr className="text-[11px] border-b-2 border-border">
                        <th className="text-center font-semibold sticky left-0 px-3 py-2.5 w-[55px]" style={{ zIndex: 30, background: 'var(--bg-hover)' }}>Mes</th>
                        <th className="text-right px-2 py-2.5 font-medium" style={{ background: 'var(--bg-hover)' }}>Uds Vend.</th>
                        <th className="text-right px-2 py-2.5 font-medium" style={{ background: 'var(--financial-subtle)' }}>Cobranza</th>
                        <th className="text-right px-2 py-2.5 font-medium" style={{ background: 'var(--financial-subtle)' }}>Crédito</th>
                        <th className="text-right px-2 py-2.5 font-medium" style={{ background: 'var(--financial-subtle)' }}>Equity</th>
                        <th className="text-right px-2 py-2.5 font-semibold" style={{ background: 'var(--financial-subtle)' }}>Total Ing.</th>
                        <th className="text-right px-2 py-2.5 font-medium" style={{ background: 'var(--construction-subtle)' }}>Costos</th>
                        <th className="text-right px-2 py-2.5 font-medium" style={{ background: 'var(--construction-subtle)' }}>Financieros</th>
                        <th className="text-right px-2 py-2.5 font-semibold" style={{ background: 'var(--construction-subtle)' }}>Total Egr.</th>
                        <th className="text-right px-2 py-2.5 font-semibold" style={{ background: 'var(--bg-hover)', color: 'var(--primary-hex)' }}>Flujo Neto</th>
                        <th className="text-right px-2 py-2.5 font-semibold" style={{ background: 'var(--bg-hover)', color: 'var(--primary-hex)' }}>Acumulado</th>
                        <th className="text-right px-2 py-2.5 font-medium" style={{ background: 'var(--bg-hover)' }}>Saldo Deuda</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedule.map((m) => {
                        const cobranza = m.revenue_effective || 0;
                        const credito = m.loan_draw || 0;
                        const equity = m.equity_contribution || 0;
                        const totalIng = cobranza + credito + equity;
                        const costos = m.total_cost || 0;
                        const financieros = (m.interest_paid || 0) + (m.loan_repayment || 0) + (m.liability_interest_paid || 0) + (m.liability_principal_paid || 0);
                        const totalEgr = costos + financieros;
                        return (
                          <tr key={m.month} className="border-b border-border/20 hover:bg-accent/30">
                            <td className="text-center font-medium tabular-nums sticky left-0 px-3 py-1.5" style={{ background: '#FFFFFF' }}>{m.month}</td>
                            <td className="text-right tabular-nums px-2 py-1.5 text-muted-foreground">{m.units_sold > 0.05 ? formatNumber(m.units_sold, 1) : '—'}</td>
                            <td className="text-right tabular-nums px-2 py-1.5">{cobranza > 0 ? formatCurrency(cobranza) : '—'}</td>
                            <td className="text-right tabular-nums px-2 py-1.5">{credito > 0 ? formatCurrency(credito) : '—'}</td>
                            <td className="text-right tabular-nums px-2 py-1.5">{equity > 0 ? formatCurrency(equity) : '—'}</td>
                            <td className="text-right tabular-nums px-2 py-1.5 font-medium">{formatCurrency(totalIng)}</td>
                            <td className="text-right tabular-nums px-2 py-1.5">{formatCurrency(costos)}</td>
                            <td className="text-right tabular-nums px-2 py-1.5">{financieros > 0 ? formatCurrency(financieros) : '—'}</td>
                            <td className="text-right tabular-nums px-2 py-1.5 font-medium">{formatCurrency(totalEgr)}</td>
                            <td className="text-right tabular-nums px-2 py-1.5 font-semibold" style={{ color: m.net_cashflow >= 0 ? 'var(--success)' : 'var(--danger)' }}>{formatCurrency(m.net_cashflow)}</td>
                            <td className="text-right tabular-nums px-2 py-1.5 font-semibold" style={{ color: m.cumulative_cashflow >= 0 ? 'var(--success)' : 'var(--danger)' }}>{formatCurrency(m.cumulative_cashflow)}</td>
                            <td className="text-right tabular-nums px-2 py-1.5 text-muted-foreground">{((m.loan_balance || 0) + (m.liability_balance || 0)) > 0 ? formatCurrency((m.loan_balance || 0) + (m.liability_balance || 0)) : '—'}</td>
                          </tr>
                        );
                      })}
                      {schedule.length > 0 && (() => {
                        const tCob = schedule.reduce((s, m) => s + (m.revenue_effective || 0), 0);
                        const tCre = schedule.reduce((s, m) => s + (m.loan_draw || 0), 0);
                        const tEq = schedule.reduce((s, m) => s + (m.equity_contribution || 0), 0);
                        const tIng = tCob + tCre + tEq;
                        const tCos = schedule.reduce((s, m) => s + (m.total_cost || 0), 0);
                        const tFin = schedule.reduce((s, m) => s + (m.interest_paid || 0) + (m.loan_repayment || 0) + (m.liability_interest_paid || 0) + (m.liability_principal_paid || 0), 0);
                        const tEgr = tCos + tFin;
                        const tNet = schedule.reduce((s, m) => s + (m.net_cashflow || 0), 0);
                        const lastCum = schedule[schedule.length - 1]?.cumulative_cashflow || 0;
                        const lastDebt = (schedule[schedule.length - 1]?.loan_balance || 0) + (schedule[schedule.length - 1]?.liability_balance || 0);
                        const tUds = schedule.reduce((s, m) => s + (m.units_sold || 0), 0);
                        return (
                          <tr className="font-semibold border-t-2 border-border">
                            <td className="text-center sticky left-0 px-3 py-2 font-bold" style={{ background: 'var(--bg-hover)' }}>TOTAL</td>
                            <td className="text-right tabular-nums px-2 py-2" style={{ background: 'var(--bg-hover)' }}>{formatNumber(tUds, 0)}</td>
                            <td className="text-right tabular-nums px-2 py-2" style={{ background: 'var(--bg-hover)' }}>{formatCurrency(tCob)}</td>
                            <td className="text-right tabular-nums px-2 py-2" style={{ background: 'var(--bg-hover)' }}>{tCre > 0 ? formatCurrency(tCre) : '—'}</td>
                            <td className="text-right tabular-nums px-2 py-2" style={{ background: 'var(--bg-hover)' }}>{formatCurrency(tEq)}</td>
                            <td className="text-right tabular-nums px-2 py-2 font-bold" style={{ background: 'var(--bg-hover)' }}>{formatCurrency(tIng)}</td>
                            <td className="text-right tabular-nums px-2 py-2" style={{ background: 'var(--bg-hover)' }}>{formatCurrency(tCos)}</td>
                            <td className="text-right tabular-nums px-2 py-2" style={{ background: 'var(--bg-hover)' }}>{tFin > 0 ? formatCurrency(tFin) : '—'}</td>
                            <td className="text-right tabular-nums px-2 py-2 font-bold" style={{ background: 'var(--bg-hover)' }}>{formatCurrency(tEgr)}</td>
                            <td className="text-right tabular-nums px-2 py-2" style={{ background: 'var(--bg-hover)', color: tNet >= 0 ? 'var(--success)' : 'var(--danger)' }}>{formatCurrency(tNet)}</td>
                            <td className="text-right tabular-nums px-2 py-2" style={{ background: 'var(--bg-hover)', color: lastCum >= 0 ? 'var(--success)' : 'var(--danger)' }}>{formatCurrency(lastCum)}</td>
                            <td className="text-right tabular-nums px-2 py-2 text-muted-foreground" style={{ background: 'var(--bg-hover)' }}>{formatCurrency(lastDebt)}</td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
                </div>
              </TabsContent>

              {/* ═══ CHARTS TAB ═══ */}
              <TabsContent value="charts" className="space-y-6">
                {/* Row 1: Net flow + Cumulative */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <Card className="rounded-xl border-border/50 shadow-sm">
                    <CardContent className="p-5">
                      <h4 className="text-sm font-display font-semibold mb-4">Flujo Neto Mensual</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} opacity={0.5} />
                          <XAxis dataKey="name" tick={AXIS_TICK} interval={tickInterval} />
                          <YAxis tick={AXIS_TICK} tickFormatter={(v) => `${(v/1e6).toFixed(0)}M`} />
                          <Tooltip content={<CustomTooltip />} />
                          <ReferenceLine y={0} stroke="#888" strokeWidth={1} />
                          <Bar dataKey="flujo_neto" name="Flujo neto" fill={COLORS.primary} radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                  <Card className="rounded-xl border-border/50 shadow-sm">
                    <CardContent className="p-5">
                      <h4 className="text-sm font-display font-semibold mb-4">Flujo Acumulado</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} opacity={0.5} />
                          <XAxis dataKey="name" tick={AXIS_TICK} interval={tickInterval} />
                          <YAxis tick={AXIS_TICK} tickFormatter={(v) => `${(v/1e6).toFixed(0)}M`} />
                          <Tooltip content={<CustomTooltip />} />
                          <ReferenceLine y={0} stroke="#888" strokeWidth={1} />
                          <defs>
                            <linearGradient id="gradCum" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.2} />
                              <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <Area type="monotone" dataKey="flujo_acumulado" name="Flujo acumulado" stroke={COLORS.primary} fill="url(#gradCum)" strokeWidth={2.5} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                {/* Sprint 3 · v2.8.4 · Estado por acreedor consolidado */}
                <CreditorStatementCard scenarioId={selectedScenario} />

                {/* Sprint 4.1 · v2.9.1 · Aforo dinámico del puente */}
                <AforoCard scenarioId={selectedScenario} projectId={selectedProject} />

                {/* Sprint 4 · v2.9.0 · Waterfall multi-tranche */}
                <WaterfallResultsCard scenarioId={selectedScenario} projectId={selectedProject} />

                {/* Row 2: Debt + Revenue vs Costs */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <Card className="rounded-xl border-border/50 shadow-sm">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <h4 className="text-sm font-display font-semibold">Deuda / Crédito Puente</h4>
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium tabular-nums">
                          Bridge + Pasivos existentes
                        </span>
                      </div>
                      <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={chartData} data-testid="debt-chart">
                          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} opacity={0.5} />
                          <XAxis dataKey="name" tick={AXIS_TICK} interval={tickInterval} />
                          <YAxis tick={AXIS_TICK} tickFormatter={(v) => `${(v/1e6).toFixed(0)}M`} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend verticalAlign="top" height={30} iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                          {/* Capa 1: Pasivo senior (índigo sólido, marca) — stacked debajo */}
                          <Area type="monotone" dataKey="liab_senior" name="Pasivo senior" stackId="debt"
                                stroke="#6366F1" fill="#6366F1" fillOpacity={0.85} strokeWidth={1.5} />
                          {/* Capa 2: Pasivo subordinada (índigo 60%) — stacked encima del senior */}
                          <Area type="monotone" dataKey="liab_subordinated" name="Pasivo subordinada" stackId="debt"
                                stroke="#6366F1" fill="#6366F1" fillOpacity={0.45} strokeWidth={1.5} strokeDasharray="4 2" />
                          {/* Capa 3: Bridge / crédito puente (warning, distinto del pasivo) — stacked encima */}
                          <Area type="monotone" dataKey="saldo_credito" name="Crédito puente" stackId="debt"
                                stroke={COLORS.warning} fill={COLORS.warning} fillOpacity={0.35} strokeWidth={1.5} />
                          {/* Línea de referencia: equity acumulado */}
                          <Line type="monotone" dataKey="equity_acum" name="Equity acumulado"
                                stroke={COLORS.success} strokeWidth={2} dot={false} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                  <Card className="rounded-xl border-border/50 shadow-sm">
                    <CardContent className="p-5">
                      <h4 className="text-sm font-display font-semibold mb-4">Ingresos vs Egresos</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} opacity={0.5} />
                          <XAxis dataKey="name" tick={AXIS_TICK} interval={tickInterval} />
                          <YAxis tick={AXIS_TICK} tickFormatter={(v) => `${(v/1e6).toFixed(0)}M`} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend verticalAlign="top" height={30} />
                          <ReferenceLine y={0} stroke="#888" strokeWidth={1} />
                          <Bar dataKey="ingresos" name="Ingresos" fill={COLORS.success} radius={[2, 2, 0, 0]} stackId="a" />
                          <Bar dataKey="egresos" name="Egresos" fill={COLORS.danger} radius={[0, 0, 2, 2]} stackId="b" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                {/* Row 3: Revenue composition + Cost composition */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  <Card className="rounded-xl border-border/50 shadow-sm">
                    <CardContent className="p-5">
                      <h4 className="text-sm font-display font-semibold mb-4">Composición de Ingresos</h4>
                      <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} opacity={0.5} />
                          <XAxis dataKey="name" tick={AXIS_TICK} interval={tickInterval} />
                          <YAxis tick={AXIS_TICK} tickFormatter={(v) => `${(v/1e6).toFixed(0)}M`} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend verticalAlign="top" height={30} />
                          <Area type="monotone" dataKey="enganche" name="Enganche/Apartado" stackId="1" stroke={COLORS.primary} fill={COLORS.primary} fillOpacity={0.6} />
                          <Area type="monotone" dataKey="parcialidades" name="Parcialidades" stackId="1" stroke={COLORS.success} fill={COLORS.success} fillOpacity={0.5} />
                          <Area type="monotone" dataKey="escrituracion" name="Escrituración" stackId="1" stroke={COLORS.warning} fill={COLORS.warning} fillOpacity={0.5} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                  <Card className="rounded-xl border-border/50 shadow-sm">
                    <CardContent className="p-5">
                      <h4 className="text-sm font-display font-semibold mb-4">Composición de Costos</h4>
                      <ResponsiveContainer width="100%" height={280}>
                        <AreaChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} opacity={0.5} />
                          <XAxis dataKey="name" tick={AXIS_TICK} interval={tickInterval} />
                          <YAxis tick={AXIS_TICK} tickFormatter={(v) => `${(v/1e6).toFixed(0)}M`} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend verticalAlign="top" height={30} />
                          <Area type="monotone" dataKey="land" name="Terreno" stackId="1" stroke={COLORS.muted} fill={COLORS.muted} fillOpacity={0.5} />
                          <Area type="monotone" dataKey="hard" name="Costo duro" stackId="1" stroke={COLORS.danger} fill={COLORS.danger} fillOpacity={0.4} />
                          <Area type="monotone" dataKey="soft" name="Costo blando" stackId="1" stroke={COLORS.warning} fill={COLORS.warning} fillOpacity={0.4} />
                          <Area type="monotone" dataKey="indirect" name="Indirectos" stackId="1" stroke={COLORS.primary} fill={COLORS.primary} fillOpacity={0.3} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* ═══ CORRIDA FINANCIERA TAB ═══ */}
              {/* ═══ SCENARIOS TAB ═══ */}
              <TabsContent value="scenarios" className="space-y-6">
                {/* Scenario management */}
                <Card className="rounded-xl border-border/50 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-sm font-display font-semibold">Gestión de Escenarios</h4>
                        <p className="text-xs text-muted-foreground mt-0.5">Duplica, compara y simula variaciones</p>
                      </div>
                      {selectedScenario && (
                        <Button size="sm" variant="outline" onClick={handleDuplicate} data-testid="duplicate-scenario-btn">
                          <Plus className="h-3.5 w-3.5 mr-1.5" /> Duplicar escenario actual
                        </Button>
                      )}
                    </div>
                    <div className="rounded-xl border border-border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/30 text-[11px]">
                            <TableHead className="w-8"></TableHead>
                            <TableHead>Escenario</TableHead>
                            <TableHead className="text-center w-20">Baseline</TableHead>
                            <TableHead className="text-center w-20">Bloqueado</TableHead>
                            <TableHead className="text-right w-24">Versión</TableHead>
                            <TableHead className="text-right w-32">Acciones</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {scenarios.map((s) => {
                            const isSelected = compareIds.includes(s.id);
                            const isCurrent = s.id === selectedScenario;
                            return (
                              <TableRow key={s.id} className={`text-xs ${isCurrent ? 'bg-accent/20' : 'hover:bg-accent/30'}`}>
                                <TableCell className="text-center">
                                  <input type="checkbox" checked={isSelected}
                                    className="rounded border-border"
                                    onChange={(e) => {
                                      if (e.target.checked) setCompareIds(prev => [...prev, s.id]);
                                      else setCompareIds(prev => prev.filter(id => id !== s.id));
                                    }} />
                                </TableCell>
                                <TableCell>
                                  <span className="font-medium">{s.name}</span>
                                  {isCurrent && <Badge variant="outline" className="ml-2 text-[9px] px-1.5">actual</Badge>}
                                </TableCell>
                                <TableCell className="text-center">{s.is_baseline ? <span style={{color:'var(--success)'}}>Base</span> : '—'}</TableCell>
                                <TableCell className="text-center">{s.is_locked ? <span style={{color:'var(--warning-color)'}}>Bloq.</span> : '—'}</TableCell>
                                <TableCell className="text-right tabular-nums">v{s.version || 1}</TableCell>
                                <TableCell className="text-right">
                                  <Button size="sm" variant="ghost" className="h-6 text-xs px-2"
                                    onClick={() => { setSelectedScenario(s.id); setAnalysis(null); }}>
                                    Seleccionar
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                    {compareIds.length >= 2 && (
                      <div className="mt-4 flex items-center gap-3">
                        <Badge variant="secondary" className="text-xs">{compareIds.length} seleccionados</Badge>
                        <Button size="sm" onClick={handleCompare} disabled={compareLoading} data-testid="compare-btn"
                          style={{ background: 'var(--primary-hex)' }} className="text-white hover:opacity-90">
                          {compareLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <BarChart3 className="h-4 w-4 mr-1.5" />}
                          Comparar escenarios
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setCompareIds([])}>Limpiar</Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Comparison results */}
                {compareData?.scenarios?.length > 0 && (
                  <Card className="rounded-xl border-border/50 shadow-sm">
                    <CardContent className="p-5">
                      <h4 className="text-sm font-display font-semibold mb-4">Comparación lado a lado</h4>
                      <div className="overflow-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/30 text-[11px]">
                              <TableHead className="min-w-[160px] font-semibold">Métrica</TableHead>
                              {compareData.scenarios.map((s) => (
                                <TableHead key={s.scenario_id} className="text-right min-w-[140px]">{s.scenario_name}</TableHead>
                              ))}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {[
                              { key: 'irr_annual', label: 'TIR del inversionista', fmt: 'pct' },
                              { key: 'npv', label: 'VAN del inversionista', fmt: 'cur' },
                              { key: 'npv_project', label: 'VAN del proyecto', fmt: 'cur' },
                              { key: 'irr_project', label: 'TIR del proyecto', fmt: 'pct' },
                              { key: 'roi', label: 'ROI s/ Capital', fmt: 'pct' },
                              { key: 'moic', label: 'MOIC', fmt: 'x' },
                              { key: 'total_profit', label: 'Utilidad Total', fmt: 'cur' },
                              { key: 'total_revenue', label: 'Ingreso Total', fmt: 'cur' },
                              { key: 'total_cost', label: 'Costo Total', fmt: 'cur' },
                              { key: 'peak_equity', label: 'Capital Máx.', fmt: 'cur' },
                              { key: 'peak_debt', label: 'Peak Debt', fmt: 'cur' },
                              { key: 'payback_month', label: 'Payback', fmt: 'mo' },
                              { key: 'breakeven_units', label: 'P.E. Unidades', fmt: 'ud' },
                              { key: 'critical_liquidity_month', label: 'Mes Crítico', fmt: 'mo' },
                              { key: 'margen_neto', label: 'Margen Neto', fmt: 'pct' },
                            ].map((row) => (
                              <TableRow key={row.key} className="text-xs hover:bg-accent/30">
                                <TableCell className="font-medium text-muted-foreground">{row.label}</TableCell>
                                {compareData.scenarios.map((s, si) => {
                                  const val = s.metrics?.[row.key];
                                  const delta = si > 0 ? s.deltas_vs_base?.[row.key] : null;
                                  const fmtVal = val == null ? '—'
                                    : row.fmt === 'pct' ? formatPercent(val)
                                    : row.fmt === 'cur' ? formatCurrency(val)
                                    : row.fmt === 'x' ? `${formatNumber(val, 2)}x`
                                    : row.fmt === 'mo' ? `Mes ${val}`
                                    : row.fmt === 'ud' ? `${formatNumber(val, 0)} uds`
                                    : formatNumber(val, 2);
                                  return (
                                    <TableCell key={s.scenario_id} className="text-right tabular-nums">
                                      <span>{fmtVal}</span>
                                      {delta != null && (
                                        <span className="ml-1.5 text-[10px]" style={{ color: delta >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                                          {delta >= 0 ? '+' : ''}{delta.toFixed(1)}%
                                        </span>
                                      )}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      {/* Alerts comparison */}
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {compareData.scenarios.map((s) => (
                          <div key={s.scenario_id} className="p-3 rounded-lg bg-muted/20 border border-border/50">
                            <p className="text-xs font-display font-semibold mb-2">{s.scenario_name}</p>
                            {(s.alerts || []).filter(a => a.severity !== 'info').length === 0 ? (
                              <p className="text-xs text-muted-foreground">Sin alertas de riesgo</p>
                            ) : (
                              <div className="space-y-1">
                                {(s.alerts || []).filter(a => a.severity !== 'info').map((a, i) => (
                                  <div key={i} className="flex items-start gap-1.5 text-xs">
                                    <span className="w-1.5 h-1.5 rounded-full mt-1 shrink-0"
                                      style={{ background: a.severity === 'critical' ? 'var(--danger)' : 'var(--warning-color)' }} />
                                    <span className="text-muted-foreground">{a.title}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="corrida">
                {analysis?.corrida ? (
                  <div className="space-y-3">
                    <div className="flex justify-end">
                      <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => handleExportExcel('corrida_financiera')}>
                        <Download className="h-3.5 w-3.5" /> Exportar Excel
                      </Button>
                    </div>
                    <CorridaFinanciera corrida={analysis.corrida} />
                  </div>
                ) : (
                  <Card className="rounded-xl border-border/50">
                    <CardContent className="py-12 text-center">
                      <p className="text-sm text-muted-foreground">La corrida financiera no está disponible. Ejecuta el análisis primero.</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* ═══ SENSITIVITY TAB ═══ */}
              <TabsContent value="sensitivity" className="space-y-6">
                {/* Single-variable sensitivity */}
                <Card className="rounded-xl border-border/50 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-sm font-display font-semibold">Sensibilidad Univariable</h4>
                        <p className="text-xs text-muted-foreground mt-0.5" data-testid="sensitivity-range-label">
                          {sensitivityData?.range_pct != null
                            ? `Impacto de una variable en los indicadores clave a ±${(sensitivityData.range_pct * 100).toLocaleString('es-MX', { maximumFractionDigits: 2 })}%${RANGE_SOURCE_LABEL[sensitivityData.range_source] ? ` · ${RANGE_SOURCE_LABEL[sensitivityData.range_source]}` : ''}`
                            : 'Impacto de una variable en los indicadores clave, al rango configurado'}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Select value={sensitivityVar} onValueChange={setSensitivityVar}>
                          <SelectTrigger className="w-[220px] h-9" data-testid="sensitivity-var-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {SENSITIVITY_VARIABLES.map(v => <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button size="sm" onClick={handleRunSensitivity} disabled={sensitivityLoading} data-testid="run-sensitivity-btn"
                          style={{ background: 'var(--primary-hex)' }} className="text-white hover:opacity-90">
                          {sensitivityLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />}
                          Ejecutar
                        </Button>
                      </div>
                    </div>
                    {sensitivityData?.results?.length > 0 ? (
                      <div className="space-y-4">
                        <p className="text-xs text-muted-foreground">
                          Variable: <strong>{SENSITIVITY_VARIABLES.find(v => v.key === sensitivityData.variable)?.label || sensitivityData.variable}</strong>
                          {' | '}Valor base: <strong>{formatNumber(sensitivityData.base_value, 2)}</strong>
                        </p>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                          <div>
                            <h5 className="text-xs font-medium text-muted-foreground mb-2">TIR vs Variable</h5>
                            <ResponsiveContainer width="100%" height={220}>
                              <LineChart data={sensitivityData.results}>
                                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} opacity={0.5} />
                                <XAxis dataKey="variable_value" tick={AXIS_TICK} tickFormatter={(v) => formatNumber(v, 0)} />
                                <YAxis tick={AXIS_TICK} tickFormatter={(v) => `${v?.toFixed(1)}%`} />
                                <Tooltip content={({ active, payload }) => {
                                  if (!active || !payload?.length) return null;
                                  const d = payload[0]?.payload;
                                  return (
                                    <div className="rounded-xl border border-border bg-card/95 backdrop-blur shadow-lg px-4 py-3 text-xs">
                                      <p>Valor: <strong>{formatNumber(d?.variable_value, 2)}</strong></p>
                                      <p>TIR inv.: <strong>{formatPercent(d?.irr_annual)}</strong></p>
                                      <p>VAN inv.: <strong>{formatCurrency(d?.npv)}</strong></p>
                                    </div>
                                  );
                                }} />
                                <Line type="monotone" dataKey="irr_annual" stroke={COLORS.primary} strokeWidth={2.5} dot={{ r: 3 }} name="TIR inv. %" />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                          <div>
                            <h5 className="text-xs font-medium text-muted-foreground mb-2">VAN vs Variable</h5>
                            <ResponsiveContainer width="100%" height={220}>
                              <AreaChart data={sensitivityData.results}>
                                <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} opacity={0.5} />
                                <XAxis dataKey="variable_value" tick={AXIS_TICK} tickFormatter={(v) => formatNumber(v, 0)} />
                                <YAxis tick={AXIS_TICK} tickFormatter={(v) => `${(v/1e6).toFixed(0)}M`} />
                                <Tooltip content={({ active, payload }) => {
                                  if (!active || !payload?.length) return null;
                                  const d = payload[0]?.payload;
                                  return (
                                    <div className="rounded-xl border border-border bg-card/95 backdrop-blur shadow-lg px-4 py-3 text-xs">
                                      <p>Valor: <strong>{formatNumber(d?.variable_value, 2)}</strong></p>
                                      <p>VAN inv.: <strong>{formatCurrency(d?.npv)}</strong></p>
                                      <p>Utilidad: <strong>{formatCurrency(d?.total_profit)}</strong></p>
                                    </div>
                                  );
                                }} />
                                <ReferenceLine y={0} stroke="#888" strokeWidth={1} />
                                <Area type="monotone" dataKey="npv" stroke={COLORS.success} fill={COLORS.successLight} strokeWidth={2} name="VAN inv." />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                        {/* Results table */}
                        <div className="rounded-xl border border-border overflow-hidden">
                          <div className="overflow-auto" style={{ maxHeight: '300px' }}>
                            <Table>
                              <TableHeader className="sticky top-0 z-10 bg-card">
                                <TableRow className="bg-muted/30 text-[11px]">
                                  <TableHead className="text-right">Valor</TableHead>
                                  <TableHead className="text-right">Variación</TableHead>
                                  <TableHead className="text-right">TIR inv.</TableHead>
                                  <TableHead className="text-right">VAN inv.</TableHead>
                                  <TableHead className="text-right">Utilidad</TableHead>
                                  <TableHead className="text-right">ROI</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {sensitivityData.results.map((r, i) => {
                                  const isBase = Math.abs(r.pct_change || 0) < 0.001;
                                  return (
                                    <TableRow key={i} className={`text-xs hover:bg-accent/30 ${isBase ? 'font-semibold bg-accent/20' : ''}`}>
                                      <TableCell className="text-right tabular-nums">{formatNumber(r.variable_value, 2)}</TableCell>
                                      <TableCell className="text-right tabular-nums">{r.pct_change != null ? `${(r.pct_change * 100).toFixed(0)}%` : '—'}</TableCell>
                                      <TableCell className="text-right tabular-nums">{formatPercent(r.irr_annual)}</TableCell>
                                      <TableCell className="text-right tabular-nums">{formatCurrency(r.npv)}</TableCell>
                                      <TableCell className="text-right tabular-nums">{formatCurrency(r.total_profit)}</TableCell>
                                      <TableCell className="text-right tabular-nums">{formatPercent(r.roi)}</TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="py-6 text-center">
                        <p className="text-sm text-muted-foreground">Selecciona una variable y haz click en "Ejecutar" para ver el impacto.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Tornado multi-variable */}
                <Card className="rounded-xl border-border/50 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-sm font-display font-semibold">Análisis Tornado</h4>
                        <p className="text-xs text-muted-foreground mt-0.5">Impacto simultáneo de todas las variables clave en la TIR a ±15%</p>
                      </div>
                      <Button size="sm" onClick={handleRunTornado} disabled={tornadoLoading} data-testid="run-tornado-btn"
                        style={{ background: 'var(--primary-hex)' }} className="text-white hover:opacity-90">
                        {tornadoLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />}
                        Ejecutar Tornado
                      </Button>
                    </div>
                    {tornadoData?.results?.length > 0 ? (
                      <div className="space-y-4">
                        <p className="text-xs text-muted-foreground">TIR base: <strong>{formatPercent(tornadoData.base_irr)}</strong></p>
                        <ResponsiveContainer width="100%" height={Math.max(300, tornadoData.results.length * 40)}>
                          <BarChart data={tornadoData.results} layout="vertical" margin={{ left: 120, right: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} opacity={0.5} horizontal={false} />
                            <XAxis type="number" tick={AXIS_TICK} tickFormatter={(v) => `${v.toFixed(1)}%`} />
                            <YAxis type="category" dataKey="label" tick={{ fill: 'var(--text-secondary)', fontSize: 12 }} width={110} />
                            <Tooltip content={({ active, payload }) => {
                              if (!active || !payload?.length) return null;
                              const d = payload[0]?.payload;
                              return (
                                <div className="rounded-xl border border-border bg-card/95 backdrop-blur shadow-lg px-4 py-3 text-xs">
                                  <p className="font-medium mb-1">{d?.label}</p>
                                  <p>TIR baja: <strong>{formatPercent(d?.irr_low)}</strong></p>
                                  <p>TIR alta: <strong>{formatPercent(d?.irr_high)}</strong></p>
                                  <p>Rango: <strong>{formatPercent(d?.irr_range)}</strong></p>
                                </div>
                              );
                            }} />
                            <ReferenceLine x={tornadoData.base_irr} stroke="#888" strokeWidth={2} strokeDasharray="4 4" />
                            <Bar dataKey="irr_low" name="TIR -15%" fill={COLORS.danger} radius={[4, 0, 0, 4]} barSize={20} />
                            <Bar dataKey="irr_high" name="TIR +15%" fill={COLORS.success} radius={[0, 4, 4, 0]} barSize={20} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="py-6 text-center">
                        <BarChart3 className="h-10 w-10 mx-auto mb-3" style={{ color: 'var(--primary-hex)', opacity: 0.3 }} />
                        <p className="text-sm text-muted-foreground">Haz click en "Ejecutar Tornado" para analizar el impacto de todas las variables simultáneamente.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ═══ STRESS TEST TAB (BLOQUE 6) ═══ */}
              <TabsContent value="stress" className="space-y-4">
                <StressTestPanel
                  projectId={selectedProject}
                  scenarioId={selectedScenario}
                  baseAnalysis={analysis}
                />
              </TabsContent>
            </Tabs>
          </motion.div>
        )}
      </div>
    </div>
  );
}
